/* eslint-disable @typescript-eslint/no-explicit-any */
import { flatten } from "flat";

import {
	addCampaignEventSuggestions,
	campaignSampleSuggestions,
	SENDER_SAMPLE_SUGGESTIONS,
} from "@signalist/shared-types";

import { Order, SortParams } from "@/types/types";

import { EventOutreachSchemaType, ListPerson } from "../schema/list";
import { ColumnConfig } from "../schema/columnConfig";
import { formatEventDate } from "../components/EventOutreach/formatEventDate";

/**
 * The shared event-outreach tokens (PREC-3520), formatted for the preview. The key set lives in
 * shared-types since PREC-4258, where the API validates campaigns against it too.
 */
const addEventSuggestions = (
	target: Record<string, unknown>,
	listEvent?: EventOutreachSchemaType | null
) => addCampaignEventSuggestions(target, listEvent, formatEventDate);

export const replaceWithoutValues = (value: any | undefined) => {
	if (!value) {
		return "";
	}

	const textValue = value.flatMap(
		(el: any) =>
			el.children?.map((n: any) => {
				const text = n.text || (n.character !== undefined ? `{${n.character}}` : "");

				return text;
			}) || []
	);

	let result = "";
	let isListItem = false;

	for (const text of textValue) {
		if (text === "") {
			if (result) {
				result += "\n";
			}
		} else if (text.startsWith("- ") || text.startsWith("* ")) {
			if (result) {
				result += "\n";
			}

			result += text;
			isListItem = true;
		} else if (text.startsWith("{")) {
			result += text;
		} else {
			if (result && !result.endsWith("}") && !isListItem) {
				result += "\n";
			}

			result += text;
		}
	}

	const resultTextValue = result.trim();

	return resultTextValue;
};

export const replaceWithRealValues = (
	value: any | undefined,
	inboxItem: ListPerson | null,
	suggestions: string[]
) => {
	if (!value) {
		return "";
	}

	const textValue = value.flatMap(
		(el: any) =>
			el.children?.map((n: any) => {
				const text = n.text || (n.character !== undefined ? `{${n.character}}` : "");

				return text;
			}) || []
	);

	let result = "";
	let isListItem = false;

	for (const text of textValue) {
		if (text === "") {
			if (result) {
				result += "\n";
			}
		} else if (text.startsWith("- ") || text.startsWith("* ")) {
			if (result) {
				result += "\n";
			}

			result += text;
			isListItem = true;
		} else if (text.startsWith("{")) {
			result += text;
		} else {
			if (result && !result.endsWith("}") && !isListItem) {
				result += "\n";
			}

			result += text;
		}
	}

	let resultTextValue = result.trim();

	for (const suggestion of suggestions) {
		const placeholder = `{${suggestion}}`;
		const objectPath = suggestion.split("_");
		let realValue: any = inboxItem;

		for (const path of objectPath) {
			if (realValue) {
				realValue = Array.isArray(realValue[path]) ? realValue[path][0] : realValue[path];
			}
		}

		if (resultTextValue.includes(placeholder)) {
			resultTextValue = resultTextValue.replaceAll(placeholder, String(realValue));
		}
	}

	return resultTextValue;
};

/** Drops array-valued entries from a flattened suggestion map. */
const dropArrayValues = (flattened: Record<string, unknown>): Record<string, unknown> => {
	const map: Record<string, unknown> = {};

	for (const key of Object.keys(flattened)) {
		if (!Array.isArray(flattened[key])) {
			map[key] = flattened[key];
		}
	}

	return map;
};

/**
 * Builds the flattened placeholder map (suggestion key → resolved value) for a
 * lead. Falls back to an example fixture when no lead is available so the
 * available keys can still be derived.
 */
export const getSuggestionsMap = (
	inboxItem?: ListPerson,
	sender?: boolean,
	listColumns?: ColumnConfig[],
	listEvent?: EventOutreachSchemaType | null
): Record<string, unknown> => {
	// Without a lead, the sample map the API validates campaigns against (PREC-4258).
	if (!inboxItem) {
		return campaignSampleSuggestions({
			sender,
			columns: listColumns,
			event: listEvent,
			formatDate: formatEventDate,
		});
	}

	const mySuggestions: any = {
		prospect: inboxItem.prospect,
	};

	if (sender) {
		Object.assign(mySuggestions, SENDER_SAMPLE_SUGGESTIONS);
	}

	if (inboxItem.liProfileEntryResult) {
		mySuggestions.liProfileEntryResult = {
			content: inboxItem.liProfileEntryResult?.content,
			contentUrl: inboxItem.liProfileEntryResult?.contentUrl,
			contentResponse: inboxItem.liProfileEntryResult?.contentResponse,
			contentResponseUrl: inboxItem.liProfileEntryResult?.contentResponseUrl,
			contentDate: inboxItem.liProfileEntryResult?.contentDate,
		};
	}

	if (inboxItem.liPostResult) {
		mySuggestions.liPostResult = {
			content: inboxItem.liPostResult?.content,
			contentUrl: inboxItem.liPostResult?.contentUrl,
			contentDate: inboxItem.liPostResult?.contentDate,
			likes: inboxItem.liPostResult?.likes,
			comments: inboxItem.liPostResult?.comments,
			shares: inboxItem.liPostResult?.shares,
			authorName: inboxItem.liPostResult?.authorName,
			authorFirstName: inboxItem.liPostResult?.authorFirstName,
			authorLastName: inboxItem.liPostResult?.authorLastName,
			authorCompanyName: inboxItem.liPostResult?.authorCompanyName,
			authorCompanyLinkedinUrl: inboxItem.liPostResult?.authorCompanyLinkedinUrl,
			authorLinkedinUrl: inboxItem.liPostResult?.authorLinkedinUrl,
		};
	}

	if (inboxItem.liEventEntry) {
		mySuggestions.liEventEntry = inboxItem.liEventEntry;
	}

	if (inboxItem.liCompanyActivityEntryResult) {
		mySuggestions.liCompanyActivityEntryResult = {
			content: inboxItem.liCompanyActivityEntryResult?.content,
			contentUrl: inboxItem.liCompanyActivityEntryResult?.contentUrl,
			contentResponse: inboxItem.liCompanyActivityEntryResult?.contentResponse,
			contentResponseUrl: inboxItem.liCompanyActivityEntryResult?.contentResponseUrl,
			contentDate: inboxItem.liCompanyActivityEntryResult?.contentDate,
		};
	}

	if (inboxItem.offer) {
		mySuggestions.offer = inboxItem.offer;
	}

	if (inboxItem.mapBusiness) {
		mySuggestions.mapBusiness = inboxItem.mapBusiness;
	}

	if (inboxItem.list.columns && inboxItem.list.columns.length > 0) {
		inboxItem.list.columns.forEach((column) => {
			mySuggestions[`column_${column.id}_${column.name}`] = column.name;
		});
	}

	if (inboxItem.columns && inboxItem.columns.length > 0) {
		inboxItem.columns.forEach((column) => {
			const columnValue = column.value?.answer ?? column.value;

			mySuggestions[
				`column_${column.columnConfigId}_${column.columnConfig.name}|${column.columnConfig.name}`
			] = columnValue;
			// Also add clean key (without |label) so placeholder validation matches
			// extractPlaceholderVarNames strips the |label part from {column_2097_time|time}
			mySuggestions[`column_${column.columnConfigId}_${column.columnConfig.name}`] = columnValue;
		});
	}

	addEventSuggestions(mySuggestions, listEvent ?? inboxItem.list?.event);

	return dropArrayValues(flatten(mySuggestions, { delimiter: "_" }));
};

export const getSuggestions = (
	inboxItem?: ListPerson,
	sender?: boolean,
	listColumns?: ColumnConfig[],
	listEvent?: EventOutreachSchemaType | null
) => Object.keys(getSuggestionsMap(inboxItem, sender, listColumns, listEvent));

export const getOrderStatus = (sortingInbox: SortParams, orderTitle: string) => {
	const hasCurrentSorting = sortingInbox.sortBy === orderTitle;

	let orderStatus = 0;

	if (hasCurrentSorting && sortingInbox.sortOrder === Order.ASC) {
		orderStatus = 1;
	}

	if (hasCurrentSorting && sortingInbox.sortOrder === Order.DESC) {
		orderStatus = 2;
	}

	return orderStatus;
};

export const formatEmployees = (
	from: number | null | undefined,
	to: number | null | undefined
): string | null => {
	if (from && to) {
		return `${from} – ${to}`;
	}

	if (to) {
		return `below ${to}`;
	}

	if (from) {
		return `${from}+`;
	}

	return null;
};
